const CUSTOMER_LOGIN_ATTEMPT_KEY = 'tytax_customer_login_attempt_v1';
const CUSTOMER_LOGIN_ATTEMPT_MAX_AGE_MS = 10 * 60 * 1000;
const LOGIN_STAPE_EVENT = 'login_stape';
const LOGIN_EVENT_ID_PREFIX = 'tytax_login';

/**
 * Records that this tab initiated a customer-account authentication flow.
 *
 * @param {Storage} storage
 * @param {number} now
 */
export function markCustomerLoginAttempt(storage = window.sessionStorage, now = Date.now()) {
  try {
    storage.setItem(CUSTOMER_LOGIN_ATTEMPT_KEY, JSON.stringify({ created_at: now }));
  } catch (error) {
    // Tracking is optional; continue with the customer login flow if storage is unavailable.
  }
}

/**
 * Consumes a valid, same-tab customer-account authentication attempt.
 *
 * @param {Storage} storage
 * @param {number} now
 * @returns {boolean}
 */
export function consumeCustomerLoginAttempt(storage = window.sessionStorage, now = Date.now()) {
  try {
    const value = storage.getItem(CUSTOMER_LOGIN_ATTEMPT_KEY);
    if (!value) return false;

    storage.removeItem(CUSTOMER_LOGIN_ATTEMPT_KEY);

    const attempt = JSON.parse(value);
    return (
      typeof attempt?.created_at === 'number' &&
      Number.isFinite(attempt.created_at) &&
      attempt.created_at <= now &&
      now - attempt.created_at <= CUSTOMER_LOGIN_ATTEMPT_MAX_AGE_MS
    );
  } catch (error) {
    return false;
  }
}

/**
 * @param {number} now
 */
export function createLoginEventId(now = Date.now()) {
  const randomValues = new Uint32Array(2);
  const crypto = window.crypto || window.msCrypto;

  if (typeof crypto?.getRandomValues === 'function') {
    crypto.getRandomValues(randomValues);
  } else {
    randomValues[0] = Math.floor(Math.random() * 0xffffffff);
    randomValues[1] = Math.floor(Math.random() * 0xffffffff);
  }

  return [
    LOGIN_EVENT_ID_PREFIX,
    now.toString(36),
    randomValues[0].toString(36),
    randomValues[1].toString(36),
  ].join('_');
}

/**
 * Emits one GTM-facing event only after Shopify has server-rendered an authenticated customer.
 *
 * @param {{ isAuthenticated: boolean, storage?: Storage, dataLayer?: Array<object>, now?: number, createEventId?: (now: number) => string }} options
 * @returns {boolean}
 */
export function publishSuccessfulCustomerLogin({
  isAuthenticated,
  storage = window.sessionStorage,
  dataLayer = window.dataLayer,
  now = Date.now(),
  createEventId = createLoginEventId,
}) {
  if (!isAuthenticated) {
    consumeCustomerLoginAttempt(storage, now);
    return false;
  }

  if (!consumeCustomerLoginAttempt(storage, now)) return false;

  const eventData = {
    event: LOGIN_STAPE_EVENT,
    method: 'customer_accounts',
    source: 'customer-account-auth',
    event_id: createEventId(now),
  };

  try {
    (dataLayer || (window.dataLayer = [])).push(eventData);
    return true;
  } catch (error) {
    return false;
  }
}

function initializeCustomerLoginTracking() {
  const config = window.__customerLoginTracking;
  if (!config) return;

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      if (event.target.closest('[data-customer-login-attempt], account-login-actions')) {
        markCustomerLoginAttempt();
      }
    },
    true
  );

  publishSuccessfulCustomerLogin({ isAuthenticated: config.isAuthenticated });
}

initializeCustomerLoginTracking();
